import { isVenueProductExcludedFromGrossProfit } from './excludeFromGrossProfit'

const toId = value => {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    const id = value._id ?? value.id
    return id == null || id === '' ? '' : String(id)
  }
  return String(value)
}

/**
 * Same venue-product match as Gross Profit spend: catalogue product, then
 * supplier when the shopping-list row has one. No catalogue product → null
 * (ad-hoc specials stay in GP). A special that still has the catalogue
 * product matches the shopping-list row like a normal line.
 */
export const findVenueProductForOrderLine = (
  venueProducts = [],
  order = {},
) => {
  const productId = toId(order?.product)
  if (!productId) return null

  const supplierId = toId(order?.supplier)
  const matches = (venueProducts || []).filter(
    venueProduct => toId(venueProduct.product) === productId,
  )
  if (!matches.length) return null

  if (supplierId) {
    const exactSupplier = matches.find(
      venueProduct => toId(venueProduct.supplier) === supplierId,
    )
    if (exactSupplier) return exactSupplier

    const anySupplier = matches.find(
      venueProduct => !toId(venueProduct.supplier),
    )
    return anySupplier || null
  }

  const anySupplier = matches.find(venueProduct => !toId(venueProduct.supplier))
  if (anySupplier) return anySupplier
  return matches.length === 1 ? matches[0] : null
}

export const isReceiptingLineExcludedFromGrossProfit = (venueProducts, order) =>
  isVenueProductExcludedFromGrossProfit(
    findVenueProductForOrderLine(venueProducts, order),
  )

export const orderLineWithParentSupplier = (
  order = {},
  parentOrder = null,
) => ({
  ...order,
  supplier: order?.supplier || parentOrder?.supplier,
})

export const canAddGrLineToShoppingList = (order = {}) =>
  Boolean(toId(order?.product))

export const buildVenueProductCreatePayloadFromGrLine = (
  order = {},
  venueId,
  parentOrder = null,
) => {
  const productId = toId(order?.product)
  if (!productId || !venueId) return null

  const product =
    typeof order.product === 'object' && order.product ? order.product : {}
  const supplierSource = order.supplier || parentOrder?.supplier
  const supplierId = toId(supplierSource)
  const supplierTitle = String(
    (typeof supplierSource === 'object' && supplierSource?.title) ||
      parentOrder?.supplierTitle ||
      '',
  ).trim()
  const title = String(order.title || product.title || '').trim() || 'Product'

  const payload = {
    title,
    product: productId,
    venue: venueId,
    zone:
      String(product.zone || order.zone || 'Default Zone').trim() ||
      'Default Zone',
    type:
      String(product.type || order.type || 'Default Type').trim() ||
      'Default Type',
    defaultQuantity: 1,
    parLevel: 0,
    excludeFromGrossProfit: false,
    isFavourite: false,
    isSpecialProduct: false,
    nominalCode: '',
    secondaryZone: '',
  }

  const uom = product.UoM || order.UoM
  if (uom) payload.UoM = uom
  if (supplierId) payload.supplier = supplierId
  if (supplierTitle) payload.supplierTitle = supplierTitle

  return payload
}
